// React Bits Count Up, https://reactbits.dev/text-animations/count-up — adapted to a
// requestAnimationFrame ease-out (no motion dependency) that animates from the previous value
// whenever `to` changes, and jumps straight there for reduced motion.

import { useEffect, useRef, useState } from 'react';

const reducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

export function useCountUp(to: number, duration = 900): number {
  const [value, setValue] = useState(0);
  const from = useRef(0);
  useEffect(() => {
    if (reducedMotion()) {
      from.current = to;
      setValue(to);
      return;
    }
    const start = performance.now();
    const origin = from.current;
    let frame = 0;
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - (1 - t) ** 3;
      const next = origin + (to - origin) * eased;
      from.current = next;
      setValue(next);
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [to, duration]);
  return value;
}

/** A number that counts up to `to`, rendered through `format`. */
export function CountUp({
  to,
  format = (n) => Math.round(n).toString(),
  duration,
  className,
}: {
  to: number;
  format?: (n: number) => string;
  duration?: number;
  className?: string;
}) {
  const value = useCountUp(to, duration);
  return (
    <span className={className} aria-label={format(to)}>
      <span aria-hidden>{format(value)}</span>
    </span>
  );
}
